import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import Modal from '@deliveroo/consumer-component-library/components/Modal';
import Button from '@deliveroo/consumer-component-library/components/Button';

import {
  trackMissedCallModalAction,
  trackSawMissedCallModal,
} from '../../actions/tracking/orderStatus';
import OrderStatusMissedCallModal from '../../components/OrderStatusMissedCallModal';
import { getMissedCallModal, getOrderStatus } from '../../selectors/orderStatusSelectors';

import { LOCAL_STORAGE_ORDER_STATUS_DISMISSED_MISSED_CALL_MODAL_KEY } from '../../utils/constants';
import i18n from '../../utils/i18n';
import { isFeatureActive } from '../../selectors/configSelectors';
import { OT_MISSED_CALL_MODAL } from '../../flags/features';
import styles from './styles.scss';

export class OrderStatusMissedCallModalContainer extends Component {
  state = {
    isModalOpen: false,
  };

  hasDismissedModal = () => {
    const { callTimestamp, orderId } = this.props;

    const previousDismissedModalJSON = window.localStorage.getItem(
      LOCAL_STORAGE_ORDER_STATUS_DISMISSED_MISSED_CALL_MODAL_KEY(orderId),
    );
    const previousDismissedModalData =
      previousDismissedModalJSON && JSON.parse(previousDismissedModalJSON);
    return Boolean(
      previousDismissedModalData && previousDismissedModalData.timestamp >= callTimestamp,
    );
  };

  saveDismissBannerToLocalStorage = () => {
    const { callTimestamp, orderId } = this.props;

    window.localStorage.setItem(
      LOCAL_STORAGE_ORDER_STATUS_DISMISSED_MISSED_CALL_MODAL_KEY(orderId),
      JSON.stringify({ timestamp: callTimestamp }),
    );
  };

  componentDidMount() {
    const { showModal } = this.props;
    const hasDismissedModal = this.hasDismissedModal();

    if (showModal && !hasDismissedModal) {
      this.props.trackSawMissedCallModal();
      this.setState({
        isModalOpen: true,
      });
    }
  }

  componentDidUpdate(prevProps) {
    const { showModal, callTimestamp } = this.props;

    /* if timestamp is greater than existing time show modal */
    if (showModal && !this.hasDismissedModal() && callTimestamp > prevProps.callTimestamp) {
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState({
        isModalOpen: true,
      });

      this.props.trackSawMissedCallModal();
    }
  }

  callRider = () => {
    window.open(`tel:${this.props.riderNumber}`, '_top');
    this.props.trackMissedCallModalAction('Clicked call rider');
    this.saveDismissBannerToLocalStorage();
    this.setState({
      isModalOpen: false,
    });
  };

  closeModal = () => {
    this.saveDismissBannerToLocalStorage();
    this.props.trackMissedCallModalAction('Dismissed missed call message');
    this.setState({
      isModalOpen: false,
    });
  };

  renderFooter = () => {
    const { actionButtonText, canCallRider } = this.props;
    const onPrimaryButtonClick = canCallRider ? this.callRider : this.closeModal;

    return (
      <div className={styles.buttons}>
        <Button isBlock onClick={onPrimaryButtonClick}>
          {actionButtonText}
        </Button>
        {canCallRider && (
          <Button type="secondary" isBlock spacing="small 0 0" onClick={this.closeModal}>
            {i18n.gettext('order_status_page.missed_call_modal.close')}
          </Button>
        )}
      </div>
    );
  };

  render() {
    const { showModal, appElement, title, text, riderNumber, canCallRider } = this.props;
    const { isModalOpen } = this.state;
    return (
      showModal && (
        <Modal
          isOpen={isModalOpen}
          headerTitle={title}
          renderFooter={this.renderFooter}
          onClose={this.closeModal}
          appElement={appElement}
        >
          <OrderStatusMissedCallModal
            title={title}
            text={text}
            canCallRider={canCallRider}
            riderNumber={riderNumber}
            onClickCallRider={this.callRider}
          />
        </Modal>
      )
    );
  }
}

OrderStatusMissedCallModalContainer.propTypes = {
  /**
   * text of the primary button
   */
  actionButtonText: PropTypes.string,
  appElement: PropTypes.string,
  /**
   * can call the rider
   */
  canCallRider: PropTypes.bool,
  /**
   * the time the rider called the customer
   */
  callTimestamp: PropTypes.string,
  /**
   * the id of the order
   */
  orderId: PropTypes.string.isRequired,
  /**
   * the number to call when clicking call rider
   */
  riderNumber: PropTypes.string,
  /**
   * should show the modal
   */
  showModal: PropTypes.bool,
  /**
   * modal text
   */
  text: PropTypes.string,
  /**
   * title of the modal
   */
  title: PropTypes.string,
  /**
   * function to track interacting with modal
   */
  trackMissedCallModalAction: PropTypes.func,
  /**
   * function to track seeing modal
   */
  trackSawMissedCallModal: PropTypes.func,
};

OrderStatusMissedCallModalContainer.defaultProps = {
  actionButtonText: '',
  appElement: '#__next',
  canCallRider: false,
  callTimestamp: '',
  riderNumber: '',
  showModal: false,
  text: '',
  title: '',
  trackMissedCallModalAction: () => {},
  trackSawMissedCallModal: () => {},
};

export const mapStateToProps = (state) => {
  const orderStatus = getOrderStatus(state);
  const missedCallModal = getMissedCallModal(state);
  const showModal = Boolean(missedCallModal) && isFeatureActive(state, OT_MISSED_CALL_MODAL);

  return {
    ...missedCallModal,
    showModal,
    orderId: orderStatus.order.id,
  };
};

const mapDispatchToProps = (dispatch) => ({
  trackSawMissedCallModal: () => {
    dispatch(trackSawMissedCallModal());
  },
  trackMissedCallModalAction: (eventName) => {
    dispatch(trackMissedCallModalAction(eventName));
  },
});

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(OrderStatusMissedCallModalContainer);
